import { useQueryClient } from "@tanstack/react-query";
import { useTellescopeSession, useTellescopeTanstackConfigContext } from "../context";
import { useCallback } from "react";
import {
  fetchTellescopeQuery,
  type FetchTellescopeQueryArgs,
  fetchInfiniteTellescopeQuery,
  type FetchTellescopeInfiniteQueryArgs,
  prefetchTellescopeQuery,
  type PrefetchTellescopeQueryArgs,
  prefetchTellescopeInfiniteQuery,
  type PrefetchTellescopeInfiniteQueryArgs,
  getTellescopeQueryData,
  type GetTellescopeQueryDataArgs,
  getTellescopeQueriesData,
  type GetTellescopeQueriesDataArgs,
  setTellescopeQueryData,
  type SetTellescopeQueryDataArgs,
  setTellescopeQueriesData,
  type SetTellescopeQueriesDataArgs,
  ensureTellescopeQueryData,
  type EnsureTellescopeQueryDataArgs,
  ensureTellescopeInfiniteQueryData,
  type EnsureTellescopeInfiniteQueryDataArgs,
  getTellescopeModelQueriesData,
  type GetTellescopeModelQueriesDataArgs,
  getTellescopeQueryState,
  type GetTellescopeQueryStateArgs,
  invalidateTellescopeQueries,
  type InvalidateTellescopeQueriesArgs,
  invalidateTellescopeModelQueries,
  type InvalidateTellescopeModelQueriesArgs,
  cancelTellescopeQueries,
  type CancelTellescopeQueriesArgs,
  refetchTellescopeQueries,
  type RefetchTellescopeQueriesArgs,
  resetTellescopeQueries,
  type ResetTellescopeQueriesArgs,
  removeTellescopeQueries,
  type RemoveTellescopeQueriesArgs,
  isFetchingTellescopeQueries,
  type IsFetchingTellescopeQueriesArgs,
  isMutatingTellescopeMutations,
  type IsMutatingTellescopeMutationsArgs,
  getTellescopeQueryDefaults,
  type GetTellescopeQueryDefaultsArgs,
  setTellescopeQueryDefaults,
  type SetTellescopeQueryDefaultsArgs,
  getTellescopeMutationDefaults,
  type GetTellescopeMutationDefaultsArgs,
  setTellescopeMutationDefaults,
  type SetTellescopeMutationDefaultsArgs,
  cancelTellescopeModelQueries,
  type CancelTellescopeModelQueriesArgs,
  refetchTellescopeModelQueries,
  type RefetchTellescopeModelQueriesArgs,
  removeTellescopeModelQueries,
  type RemoveTellescopeModelQueriesArgs,
  resetTellescopeModelQueries,
  type ResetTellescopeModelQueriesArgs,
  setTellescopeModelQueriesData,
  type SetTellescopeModelQueriesDataArgs,
  fetchSessionQuery,
  type FetchSessionQueryArgs,
  getSessionQueryData,
  type GetSessionQueryDataArgs,
  setSessionQueryData,
  type SetSessionQueryDataArgs,
  invalidateSessionQueries,
  type InvalidateSessionQueriesArgs,
} from "../utils";
import type {
  InfiniteQueryMethodForModel,
  ModelName,
  MethodForModel,
  MutationMethodForModel,
  QueryMethodForModel,
  ReturnForMethod,
  StandardQueryMethodForModel,
  QueryDataForMethod,
  ParamsForMethod,
  SessionMethodName,
  SessionMethodReturn,
  SessionStandardQueryMethodName,
  SessionQueryMethodName,
  SupportedSession,
} from "../types";
type TellescopeQueryClientFunctionArguments<T> = Omit<T, "session" | "queryClient">;

/**
 * Tellescope-specific query methods that wrap TanStack Query's query client methods.
 */
export interface TellescopeQueryClient {
  /**
   * Fetches a standard (non-paginated) Tellescope query and returns the data.
   * This method will always make a network request, regardless of cache state.
   *
   * @template Model - The Tellescope model name (must support standard queries)
   * @template Method - The standard query method for the model
   * @template TError - The error type (defaults to Error)
   * @template TData - The transformed data type (defaults to raw API return type)
   *
   * @param args - Configuration object containing model, method, request, and optional query options
   * @returns A promise that resolves to the query data
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * // Fetch a single user
   * const user = await queryClient.fetchQuery({
   *   model: "users",
   *   method: "getOne",
   *   request: { id: "user-123" },
   * });
   * ```
   */
  fetchQuery: <
    Model extends ModelName,
    Method extends StandardQueryMethodForModel<Model>,
    TError = Error,
    TData = ReturnForMethod<Model, Method>,
  >(
    args: TellescopeQueryClientFunctionArguments<FetchTellescopeQueryArgs<Model, Method, TError, TData>>
  ) => ReturnType<typeof fetchTellescopeQuery<Model, Method, TError, TData>>;

  /**
   * Fetches an infinite/paginated Tellescope query and returns the data.
   * This method will always make a network request, regardless of cache state.
   *
   * @template Model - The Tellescope model name (must support infinite queries)
   * @template Method - The infinite query method for the model
   * @template TPageParam - The type of the page parameter
   * @template TError - The error type (defaults to Error)
   * @template TData - The transformed data type (defaults to raw API return type)
   *
   * @param args - Configuration object containing model, method, request, page params, and optional query options
   * @returns A promise that resolves to the infinite query data
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * // Fetch paginated users
   * const infiniteData = await queryClient.fetchInfiniteQuery({
   *   model: "users",
   *   method: "getSome",
   *   request: { filter: {}, limit: 10 },
   *   initialPageParam: undefined,
   *   pages: 1,
   * });
   * ```
   */
  fetchInfiniteQuery: <
    Model extends ModelName,
    Method extends InfiniteQueryMethodForModel<Model>,
    TPageParam,
    TError = Error,
    TData = ReturnForMethod<Model, Method>,
  >(
    args: TellescopeQueryClientFunctionArguments<
      FetchTellescopeInfiniteQueryArgs<Model, Method, TPageParam, TError, TData>
    >
  ) => ReturnType<typeof fetchInfiniteTellescopeQuery<Model, Method, TPageParam, TError, TData>>;

  /**
   * Prefetches a standard (non-paginated) Tellescope query and caches the data.
   * This method will make a network request and update the cache, but won't return the data.
   * Useful for optimistically loading data before it's needed.
   *
   * @template Model - The Tellescope model name (must support standard queries)
   * @template Method - The standard query method for the model
   * @template TError - The error type (defaults to Error)
   * @template TData - The transformed data type (defaults to raw API return type)
   *
   * @param args - Configuration object containing model, method, request, and optional query options
   * @returns A promise that resolves when prefetching is complete
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * // Prefetch a user on hover
   * await queryClient.prefetchQuery({
   *   model: "users",
   *   method: "getOne",
   *   request: { id: "user-123" },
   * });
   * ```
   */
  prefetchQuery: <
    Model extends ModelName,
    Method extends StandardQueryMethodForModel<Model>,
    TError = Error,
    TData = ReturnForMethod<Model, Method>,
  >(
    args: TellescopeQueryClientFunctionArguments<PrefetchTellescopeQueryArgs<Model, Method, TError, TData>>
  ) => ReturnType<typeof prefetchTellescopeQuery<Model, Method, TError, TData>>;

  /**
   * Prefetches an infinite/paginated Tellescope query and caches the data.
   * This method will make a network request and update the cache, but won't return the data.
   * Useful for optimistically loading paginated data before it's needed.
   *
   * @template Model - The Tellescope model name (must support infinite queries)
   * @template Method - The infinite query method for the model
   * @template TPageParam - The type of the page parameter
   * @template TError - The error type (defaults to Error)
   * @template TData - The transformed data type (defaults to raw API return type)
   *
   * @param args - Configuration object containing model, method, request, page params, and optional query options
   * @returns A promise that resolves when prefetching is complete
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * // Prefetch paginated users on hover
   * await queryClient.prefetchInfiniteQuery({
   *   model: "users",
   *   method: "getSome",
   *   request: { filter: {}, limit: 10 },
   *   initialPageParam: undefined,
   *   pages: 1,
   * });
   * ```
   */
  prefetchInfiniteQuery: <
    Model extends ModelName,
    Method extends InfiniteQueryMethodForModel<Model>,
    TPageParam,
    TError = Error,
    TData = ReturnForMethod<Model, Method>,
  >(
    args: TellescopeQueryClientFunctionArguments<
      PrefetchTellescopeInfiniteQueryArgs<Model, Method, TPageParam, TError, TData>
    >
  ) => ReturnType<typeof prefetchTellescopeInfiniteQuery<Model, Method, TPageParam, TError, TData>>;

  /**
   * Retrieves cached data for a single Tellescope query.
   * Returns undefined if the query is not in the cache.
   *
   * @template Model - The Tellescope model name
   * @template Method - The query method for the model
   *
   * @param args - Configuration object containing model, method, and request
   * @returns The cached data or undefined
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * // Get cached user data
   * const user = queryClient.getQueryData({
   *   model: "users",
   *   method: "getOne",
   *   request: { id: "user-123" },
   * });
   * ```
   */
  getQueryData: <Model extends ModelName, Method extends MethodForModel<Model>>(
    args: TellescopeQueryClientFunctionArguments<GetTellescopeQueryDataArgs<Model, Method>>
  ) => ReturnType<typeof getTellescopeQueryData<Model, Method>>;

  /**
   * Retrieves cached data for all matching Tellescope queries.
   * Returns an array of [queryKey, data] tuples for all queries that match the criteria.
   *
   * @template Model - The Tellescope model name (must support query methods)
   * @template Method - The query method for the model
   * @template TError - The error type for the queries (defaults to Error)
   *
   * @param args - Configuration object containing model, method, optional request, and filters
   * @returns An array of [queryKey, data] tuples
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * // Get all cached user getOne queries
   * const allUserQueries = queryClient.getQueriesData({
   *   model: "users",
   *   method: "getOne",
   * });
   *
   * // Get specific query
   * const specificQuery = queryClient.getQueriesData({
   *   model: "users",
   *   method: "getOne",
   *   request: { id: "user-123" },
   * });
   * ```
   */
  getQueriesData: <Model extends ModelName, Method extends QueryMethodForModel<Model>, TError = Error>(
    args: TellescopeQueryClientFunctionArguments<GetTellescopeQueriesDataArgs<Model, Method, TError>>
  ) => ReturnType<typeof getTellescopeQueriesData<Model, Method, TError>>;

  /**
   * Sets cached data for a single Tellescope query.
   * Can be used to update the cache directly without making a network request.
   * For infinite/paginated queries, data should be wrapped in InfiniteData structure.
   *
   * @template Model - The Tellescope model name
   * @template Method - The query method for the model
   *
   * @param args - Configuration object containing model, method, request, updater, and optional options
   * @returns An object containing the original query data before update and the updated query data
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * // Set data directly
   * const { originalQuery, updatedQuery } = queryClient.setQueryData({
   *   model: "users",
   *   method: "getOne",
   *   request: { id: "user-123" },
   *   updater: { id: "user-123", fname: "John", lname: "Doe", email: "john@example.com" },
   * });
   * // originalQuery: [queryKey, oldData]
   * // updatedQuery: [queryKey, newData]
   *
   * // Update existing data with an updater function
   * const result = queryClient.setQueryData({
   *   model: "users",
   *   method: "getOne",
   *   request: { id: "user-123" },
   *   updater: (old) => old ? { ...old, fname: "Jane" } : undefined,
   * });
   *
   * // Set data with custom options
   * queryClient.setQueryData({
   *   model: "users",
   *   method: "getOne",
   *   request: { id: "user-123" },
   *   updater: { id: "user-123", fname: "Jane", lname: "Doe", email: "jane@example.com" },
   *   options: {
   *     updatedAt: Date.now(),
   *   },
   * });
   * ```
   */
  setQueryData: <Model extends ModelName, Method extends QueryMethodForModel<Model>>(
    args: TellescopeQueryClientFunctionArguments<SetTellescopeQueryDataArgs<Model, Method>>
  ) => ReturnType<typeof setTellescopeQueryData<Model, Method>>;

  /**
   * Sets cached data for all matching Tellescope queries.
   * Returns an object with arrays of [queryKey, data] tuples for all queries that were updated,
   * both with their original data and their new data.
   * For infinite queries, data should be wrapped in InfiniteData structure.
   *
   * @template Model - The Tellescope model name
   * @template Method - The query method for the model
   * @template TError - The error type for the queries (defaults to Error)
   *
   * @param args - Configuration object containing model, method, optional request, updater, filters, and options
   * @returns An object with originalQueries (before update) and updatedQueries (after update)
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * // Update all queries for a method
   * const { originalQueries, updatedQueries } = queryClient.setQueriesData({
   *   model: "users",
   *   method: "getOne",
   *   updater: (old) => old ? { ...old, isActive: true } : old,
   * });
   *
   * // Update specific query
   * const result = queryClient.setQueriesData({
   *   model: "users",
   *   method: "getOne",
   *   request: { id: "user-123" },
   *   updater: { id: "user-123", fname: "Jane", lname: "Doe", email: "jane@example.com" },
   * });
   *
   * // With filters
   * queryClient.setQueriesData({
   *   model: "users",
   *   method: "getOne",
   *   updater: (old) => old ? { ...old, lastUpdated: Date.now() } : old,
   *   filters: {
   *     stale: false, // Only update non-stale queries
   *   },
   * });
   *
   * // With custom options
   * queryClient.setQueriesData({
   *   model: "users",
   *   method: "getOne",
   *   updater: { id: "user-123", fname: "Jane", lname: "Doe", email: "jane@example.com" },
   *   options: {
   *     updatedAt: Date.now(),
   *   },
   * });
   * ```
   */
  setQueriesData: <Model extends ModelName, Method extends QueryMethodForModel<Model>, TError = Error>(
    args: TellescopeQueryClientFunctionArguments<SetTellescopeQueriesDataArgs<Model, Method, TError>>
  ) => ReturnType<typeof setTellescopeQueriesData<Model, Method, TError>>;

  /**
   * Sets cached data for all queries for a specific Tellescope model.
   *
   * This function provides a way to set data for all queries for a model, optionally
   * with method-specific updaters and filters. When no methodConfigs are provided, it sets
   * data for all queries for the model. When methodConfigs are provided, it only sets
   * data for queries for the specified methods.
   *
   * @template Model - The Tellescope model name (must support query methods)
   * @template TError - The error type for the queries (defaults to Error)
   *
   * @param args - Configuration object containing model, queryClient, updater, and optional filters
   * @returns An object with originalQueries (before update) and updatedQueries (after update)
   *
   * @example
   * ```ts
   * const queryClient = useTellescopeQueryClient();
   *
   * // Set data for all user queries
   * const { originalQueries, updatedQueries } = queryClient.setModelQueriesData({
   *   model: "users",
   *   updater: (old) => old ? { ...old, isActive: true } : old,
   * });
   * ```
   *
   * @example
   * ```ts
   * const queryClient = useTellescopeQueryClient();
   *
   * // Set data only for specific methods with method-specific updaters
   * const result = queryClient.setModelQueriesData({
   *   model: "users",
   *   updater: (old) => old, // Default updater (no-op)
   *   methodConfigs: {
   *     getOne: {
   *       updater: (old) => old ? { ...old, lastViewed: Date.now() } : old,
   *     },
   *     getSome: {
   *       request: { filter: { fname: "John" }, limit: 10 },
   *       updater: (old) => old ? { ...old, isStale: true } : old,
   *     },
   *   },
   * });
   * ```
   *
   * @example
   * ```ts
   * const queryClient = useTellescopeQueryClient();
   *
   * // Use global filters and options
   * queryClient.setModelQueriesData({
   *   model: "users",
   *   updater: (old) => old ? { ...old, updated: true } : old,
   *   filters: {
   *     stale: true, // Only set data for stale queries
   *   },
   *   options: {
   *     updatedAt: Date.now(),
   *   },
   * });
   * ```
   *
   * @example
   * ```ts
   * const queryClient = useTellescopeQueryClient();
   *
   * // Use method-specific options
   * queryClient.setModelQueriesData({
   *   model: "users",
   *   updater: (old) => old,
   *   methodConfigs: {
   *     getOne: {
   *       options: {
   *         updatedAt: Date.now(),
   *       },
   *     },
   *   },
   * });
   * ```
   */
  setModelQueriesData: <Model extends ModelName, TError = Error>(
    args: TellescopeQueryClientFunctionArguments<SetTellescopeModelQueriesDataArgs<Model, TError>>
  ) => ReturnType<typeof setTellescopeModelQueriesData<Model, TError>>;

  /**
   * Retrieves all cached queries for a specific Tellescope model, organized by method.
   *
   * This method provides a comprehensive view of all cached data for a model across
   * all query methods. It returns both a flat list of all queries and a grouped view
   * organized by method name.
   *
   * @template Model - The Tellescope model name (must support query methods)
   * @template TError - The error type for the queries (defaults to Error)
   *
   * @param args - Configuration object containing model and optional filters
   * @returns An object containing `allQueries` (flat array) and `queriesByMethod` (grouped by method)
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * // Get all user queries
   * const { allQueries, queriesByMethod } = queryClient.getModelQueriesData({
   *   model: "users",
   * });
   *
   * // Access queries by method
   * const getOneQueries = queriesByMethod.getOne;
   * const getSomeQueries = queriesByMethod.getSome;
   * ```
   */
  getModelQueriesData: <Model extends ModelName, TError = Error>(
    args: TellescopeQueryClientFunctionArguments<GetTellescopeModelQueriesDataArgs<Model, TError>>
  ) => ReturnType<typeof getTellescopeModelQueriesData<Model, TError>>;

  /**
   * Retrieves the query state for a single Tellescope query.
   * Returns undefined if the query is not in the cache.
   * The query state includes information about the query's status, data, error, and more.
   *
   * @template Model - The Tellescope model name
   * @template Method - The query method for the model
   *
   * @param args - Configuration object containing model, method, and request
   * @returns The query state or undefined
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * // Get query state
   * const state = queryClient.getQueryState({
   *   model: "users",
   *   method: "getOne",
   *   request: { id: "user-123" },
   * });
   *
   * if (state) {
   *   console.log("Status:", state.status);
   *   console.log("Is fetching:", state.isFetching);
   *   console.log("Is stale:", state.isStale);
   * }
   * ```
   */
  getQueryState: <Model extends ModelName, Method extends MethodForModel<Model>>(
    args: TellescopeQueryClientFunctionArguments<GetTellescopeQueryStateArgs<Model, Method>>
  ) => ReturnType<typeof getTellescopeQueryState<Model, Method>>;

  /**
   * Invalidates queries in the cache for matching Tellescope queries.
   * Invalidated queries will be marked as stale and refetched in the background if they are currently being rendered.
   *
   * @template Model - The Tellescope model name (must support query methods)
   * @template Method - The query method for the model
   * @template TError - The error type for the queries (defaults to Error)
   *
   * @param args - Configuration object containing model, method, optional request, filters, and options
   * @returns A promise that resolves when invalidation is complete
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * // Invalidate all queries for a method
   * await queryClient.invalidateQueries({
   *   model: "users",
   *   method: "getOne",
   * });
   *
   * // Invalidate specific query
   * await queryClient.invalidateQueries({
   *   model: "users",
   *   method: "getOne",
   *   request: { id: "user-123" },
   * });
   *
   * // With filters
   * await queryClient.invalidateQueries({
   *   model: "users",
   *   method: "getSome",
   *   filters: {
   *     refetchType: "active", // Only refetch active queries
   *   },
   * });
   *
   * // With custom options
   * await queryClient.invalidateQueries({
   *   model: "users",
   *   method: "getOne",
   *   request: { id: "user-123" },
   *   options: {
   *     throwOnError: true,
   *   },
   * });
   * ```
   */
  invalidateQueries: <Model extends ModelName, Method extends QueryMethodForModel<Model>, TError = Error>(
    args: TellescopeQueryClientFunctionArguments<InvalidateTellescopeQueriesArgs<Model, Method, TError>>
  ) => ReturnType<typeof invalidateTellescopeQueries<Model, Method, TError>>;

  /**
   * Invalidates all cached queries for a specific Tellescope model.
   *
   * This method provides a way to invalidate all queries for a model, optionally
   * with method-specific filters. When no methodFilters are provided, it invalidates
   * all queries for the model. When methodFilters are provided, it only invalidates
   * queries for the specified methods.
   *
   * @template Model - The Tellescope model name (must support query methods)
   * @template TError - The error type for the queries (defaults to Error)
   *
   * @param args - Configuration object containing model, optional filters, and options
   * @returns A promise that resolves when invalidation is complete
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * // Invalidate all user queries
   * await queryClient.invalidateModelQueries({
   *   model: "users",
   * });
   *
   * // Invalidate only specific methods
   * await queryClient.invalidateModelQueries({
   *   model: "users",
   *   methodFilters: {
   *     getOne: {},
   *     getSome: {
   *       request: { filter: { fname: "John" }, limit: 10 },
   *       refetchType: "active",
   *     },
   *   },
   * });
   *
   * // Use global filters and options
   * await queryClient.invalidateModelQueries({
   *   model: "users",
   *   filters: {
   *     refetchType: "none", // Don't auto-refetch
   *   },
   *   options: {
   *     throwOnError: true,
   *   },
   * });
   *
   * // Use method-specific options
   * await queryClient.invalidateModelQueries({
   *   model: "users",
   *   methodFilters: {
   *     getOne: {
   *       options: {
   *         cancelRefetch: false,
   *       },
   *     },
   *   },
   * });
   * ```
   */
  invalidateModelQueries: <Model extends ModelName, TError = Error>(
    args: TellescopeQueryClientFunctionArguments<InvalidateTellescopeModelQueriesArgs<Model, TError>>
  ) => ReturnType<typeof invalidateTellescopeModelQueries<Model, TError>>;

  /**
   * Ensures that data for a standard (non-paginated) Tellescope query exists in the cache.
   * If the data is already cached and fresh, it returns the cached data.
   * If the data is stale or doesn't exist, it fetches fresh data and returns it.
   *
   * @template Model - The Tellescope model name (must support standard queries)
   * @template Method - The standard query method for the model
   * @template TError - The error type (defaults to Error)
   * @template TData - The transformed data type (defaults to raw API return type)
   *
   * @param args - Configuration object containing model, method, request, and optional query options
   * @returns A promise that resolves to the query data
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * // Ensure user data exists (fetch if not cached or stale)
   * const user = await queryClient.ensureQueryData({
   *   model: "users",
   *   method: "getOne",
   *   request: { id: "user-123" },
   * });
   * ```
   */
  ensureQueryData: <
    Model extends ModelName,
    Method extends StandardQueryMethodForModel<Model>,
    TError = Error,
    TData = ReturnForMethod<Model, Method>,
  >(
    args: TellescopeQueryClientFunctionArguments<EnsureTellescopeQueryDataArgs<Model, Method, TError, TData>>
  ) => ReturnType<typeof ensureTellescopeQueryData<Model, Method, TError, TData>>;

  /**
   * Ensures that data for an infinite/paginated Tellescope query exists in the cache.
   * If the data is already cached and fresh, it returns the cached data.
   * If the data is stale or doesn't exist, it fetches fresh data and returns it.
   *
   * @template Model - The Tellescope model name (must support infinite queries)
   * @template Method - The infinite query method for the model
   * @template TPageParam - The type of the page parameter
   * @template TError - The error type (defaults to Error)
   * @template TData - The transformed data type (defaults to raw API return type)
   *
   * @param args - Configuration object containing model, method, request, page params, and optional query options
   * @returns A promise that resolves to the infinite query data
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * // Ensure paginated users data exists
   * const infiniteData = await queryClient.ensureInfiniteQueryData({
   *   model: "users",
   *   method: "getSome",
   *   request: { filter: {}, limit: 10 },
   *   initialPageParam: undefined,
   *   pages: 1,
   * });
   * ```
   */
  ensureInfiniteQueryData: <
    Model extends ModelName,
    Method extends InfiniteQueryMethodForModel<Model>,
    TPageParam,
    TError = Error,
    TData = ReturnForMethod<Model, Method>,
  >(
    args: TellescopeQueryClientFunctionArguments<
      EnsureTellescopeInfiniteQueryDataArgs<Model, Method, TPageParam, TError, TData>
    >
  ) => ReturnType<typeof ensureTellescopeInfiniteQueryData<Model, Method, TPageParam, TError, TData>>;

  /**
   * Cancels outgoing queries for matching Tellescope queries.
   * This is useful for canceling requests when a component unmounts or when you want to prevent unnecessary network requests.
   *
   * @template Model - The Tellescope model name (must support query methods)
   * @template Method - The query method for the model
   * @template TError - The error type for the queries (defaults to Error)
   *
   * @param args - Configuration object containing model, method, optional request, filters, and options
   * @returns A promise that resolves when cancellation is complete
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * // Cancel all queries for a method
   * await queryClient.cancelQueries({
   *   model: "users",
   *   method: "getOne",
   * });
   *
   * // Cancel specific query
   * await queryClient.cancelQueries({
   *   model: "users",
   *   method: "getOne",
   *   request: { id: "user-123" },
   * });
   *
   * // Cancel fetching queries
   * await queryClient.cancelQueries({
   *   model: "users",
   *   method: "getOne",
   *   filters: {
   *     fetchStatus: "fetching",
   *   },
   * });
   * ```
   */
  cancelQueries: <Model extends ModelName, Method extends QueryMethodForModel<Model>, TError = Error>(
    args: TellescopeQueryClientFunctionArguments<CancelTellescopeQueriesArgs<Model, Method, TError>>
  ) => ReturnType<typeof cancelTellescopeQueries<Model, Method, TError>>;

  /**
   * Cancels all outgoing queries for a specific Tellescope model.
   *
   * This function provides a way to cancel all queries for a model, optionally
   * with method-specific filters. When no methodFilters are provided, it cancels
   * all queries for the model. When methodFilters are provided, it only cancels
   * queries for the specified methods.
   *
   * @template Model - The Tellescope model name (must support query methods)
   * @template TError - The error type for the queries (defaults to Error)
   *
   * @param args - Configuration object containing model, queryClient, and optional filters
   * @returns A promise that resolves when cancellation is complete
   *
   * @example
   * ```ts
   * const queryClient = useTellescopeQueryClient();
   *
   * // Cancel all user queries
   * await queryClient.cancelModelQueries({
   *   model: "users",
   * });
   * ```
   *
   * @example
   * ```ts
   * const queryClient = useTellescopeQueryClient();
   *
   * // Cancel only specific methods
   * await queryClient.cancelModelQueries({
   *   model: "users",
   *   methodFilters: {
   *     getOne: {
   *       // Cancel all getOne queries
   *     },
   *     getSome: {
   *       request: { filter: { fname: "John" }, limit: 10 },
   *     },
   *   },
   * });
   * ```
   *
   * @example
   * ```ts
   * const queryClient = useTellescopeQueryClient();
   *
   * // Use global filters with method-specific filters
   * await queryClient.cancelModelQueries({
   *   model: "users",
   *   filters: {
   *     fetchStatus: "fetching", // Only cancel fetching queries
   *   },
   *   methodFilters: {
   *     getOne: {
   *       // Inherits fetchStatus: "fetching" from global filters
   *     },
   *   },
   * });
   * ```
   *
   * @example
   * ```ts
   * const queryClient = useTellescopeQueryClient();
   *
   * // Use global and method-specific options
   * await queryClient.cancelModelQueries({
   *   model: "users",
   *   options: {
   *     revert: true, // Global option for all methods
   *   },
   *   methodFilters: {
   *     getOne: {
   *       options: {
   *         silent: true, // Method-specific option (overrides global)
   *       },
   *     },
   *   },
   * });
   * ```
   */
  cancelModelQueries: <Model extends ModelName, TError = Error>(
    args: TellescopeQueryClientFunctionArguments<CancelTellescopeModelQueriesArgs<Model, TError>>
  ) => ReturnType<typeof cancelTellescopeModelQueries<Model, TError>>;

  /**
   * Refetches queries in the query client cache for matching Tellescope queries.
   * Refetched queries will be re-executed and their data updated.
   *
   * @template Model - The Tellescope model name (must support query methods)
   * @template Method - The query method for the model
   * @template TError - The error type for the queries (defaults to Error)
   *
   * @param args - Configuration object containing model, method, optional request, filters, and options
   * @returns A promise that resolves when refetching is complete
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * // Refetch all queries for a method
   * await queryClient.refetchQueries({
   *   model: "users",
   *   method: "getOne",
   * });
   *
   * // Refetch specific query
   * await queryClient.refetchQueries({
   *   model: "users",
   *   method: "getOne",
   *   request: { id: "user-123" },
   * });
   *
   * // Refetch stale queries only
   * await queryClient.refetchQueries({
   *   model: "users",
   *   method: "getOne",
   *   filters: {
   *     stale: true,
   *   },
   * });
   *
   * // Refetch with custom options
   * await queryClient.refetchQueries({
   *   model: "users",
   *   method: "getOne",
   *   options: {
   *     throwOnError: true,
   *   },
   * });
   * ```
   */
  refetchQueries: <Model extends ModelName, Method extends QueryMethodForModel<Model>, TError = Error>(
    args: TellescopeQueryClientFunctionArguments<RefetchTellescopeQueriesArgs<Model, Method, TError>>
  ) => ReturnType<typeof refetchTellescopeQueries<Model, Method, TError>>;

  /**
   * Refetches all cached queries for a specific Tellescope model.
   *
   * This function provides a way to refetch all queries for a model, optionally
   * with method-specific filters. When no methodFilters are provided, it refetches
   * all queries for the model. When methodFilters are provided, it only refetches
   * queries for the specified methods.
   *
   * @template Model - The Tellescope model name (must support query methods)
   * @template TError - The error type for the queries (defaults to Error)
   *
   * @param args - Configuration object containing model, queryClient, and optional filters
   * @returns A promise that resolves when refetching is complete
   *
   * @example
   * ```ts
   * const queryClient = useTellescopeQueryClient();
   *
   * // Refetch all user queries
   * await queryClient.refetchModelQueries({
   *   model: "users",
   * });
   * ```
   *
   * @example
   * ```ts
   * const queryClient = useTellescopeQueryClient();
   *
   * // Refetch only specific methods
   * await queryClient.refetchModelQueries({
   *   model: "users",
   *   methodFilters: {
   *     getOne: {
   *       // Refetch all getOne queries
   *     },
   *     getSome: {
   *       request: { filter: { fname: "John" }, limit: 10 },
   *     },
   *   },
   * });
   * ```
   *
   * @example
   * ```ts
   * const queryClient = useTellescopeQueryClient();
   *
   * // Use global filters with method-specific filters
   * await queryClient.refetchModelQueries({
   *   model: "users",
   *   filters: {
   *     stale: true, // Only refetch stale queries
   *   },
   *   methodFilters: {
   *     getOne: {
   *       // Inherits stale: true from global filters
   *     },
   *   },
   * });
   * ```
   *
   * @example
   * ```ts
   * const queryClient = useTellescopeQueryClient();
   *
   * // Use global and method-specific options
   * await queryClient.refetchModelQueries({
   *   model: "users",
   *   options: {
   *     throwOnError: true, // Global option for all methods
   *   },
   *   methodFilters: {
   *     getOne: {
   *       options: {
   *         cancelRefetch: false, // Method-specific option (overrides global)
   *       },
   *     },
   *   },
   * });
   * ```
   */
  refetchModelQueries: <Model extends ModelName, TError = Error>(
    args: TellescopeQueryClientFunctionArguments<RefetchTellescopeModelQueriesArgs<Model, TError>>
  ) => ReturnType<typeof refetchTellescopeModelQueries<Model, TError>>;

  /**
   * Resets queries in the query client cache for matching Tellescope queries.
   * This resets the queries to their initial state and refetches them if they are currently being rendered.
   *
   * @template Model - The Tellescope model name (must support query methods)
   * @template Method - The query method for the model
   * @template TError - The error type for the queries (defaults to Error)
   *
   * @param args - Configuration object containing model, method, optional request, filters, and options
   * @returns A promise that resolves when reset is complete
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * // Reset all queries for a method
   * await queryClient.resetQueries({
   *   model: "users",
   *   method: "getOne",
   * });
   *
   * // Reset specific query
   * await queryClient.resetQueries({
   *   model: "users",
   *   method: "getOne",
   *   request: { id: "user-123" },
   * });
   *
   * // Reset with options
   * await queryClient.resetQueries({
   *   model: "users",
   *   method: "getOne",
   *   request: { id: "user-123" },
   *   options: {
   *     throwOnError: true,
   *   },
   * });
   * ```
   */
  resetQueries: <Model extends ModelName, Method extends QueryMethodForModel<Model>, TError = Error>(
    args: TellescopeQueryClientFunctionArguments<ResetTellescopeQueriesArgs<Model, Method, TError>>
  ) => ReturnType<typeof resetTellescopeQueries<Model, Method, TError>>;

  /**
   * Resets all cached queries for a specific Tellescope model.
   *
   * This function provides a way to reset all queries for a model, optionally
   * with method-specific filters. When no methodFilters are provided, it resets
   * all queries for the model. When methodFilters are provided, it only resets
   * queries for the specified methods.
   *
   * @template Model - The Tellescope model name (must support query methods)
   * @template TError - The error type for the queries (defaults to Error)
   *
   * @param args - Configuration object containing model, queryClient, and optional filters
   * @returns A promise that resolves when reset is complete
   *
   * @example
   * ```ts
   * const queryClient = useTellescopeQueryClient();
   *
   * // Reset all user queries
   * await queryClient.resetModelQueries({
   *   model: "users",
   * });
   * ```
   *
   * @example
   * ```ts
   * const queryClient = useTellescopeQueryClient();
   *
   * // Reset only specific methods
   * await queryClient.resetModelQueries({
   *   model: "users",
   *   methodFilters: {
   *     getOne: {
   *       // Reset all getOne queries
   *     },
   *     getSome: {
   *       request: { filter: { fname: "John" }, limit: 10 },
   *     },
   *   },
   * });
   * ```
   *
   * @example
   * ```ts
   * const queryClient = useTellescopeQueryClient();
   *
   * // Use global filters with method-specific filters
   * await queryClient.resetModelQueries({
   *   model: "users",
   *   filters: {
   *     stale: true, // Only reset stale queries
   *   },
   *   methodFilters: {
   *     getOne: {
   *       // Inherits stale: true from global filters
   *     },
   *   },
   * });
   * ```
   *
   * @example
   * ```ts
   * const queryClient = useTellescopeQueryClient();
   *
   * // Use global and method-specific options
   * await queryClient.resetModelQueries({
   *   model: "users",
   *   options: {
   *     throwOnError: true, // Global option for all methods
   *   },
   *   methodFilters: {
   *     getOne: {
   *       options: {
   *         throwOnError: false, // Method-specific option (overrides global)
   *       },
   *     },
   *   },
   * });
   * ```
   */
  resetModelQueries: <Model extends ModelName, TError = Error>(
    args: TellescopeQueryClientFunctionArguments<ResetTellescopeModelQueriesArgs<Model, TError>>
  ) => ReturnType<typeof resetTellescopeModelQueries<Model, TError>>;

  /**
   * Removes queries from the query client cache for matching Tellescope queries.
   * This completely removes the queries from the cache, including their data and state.
   *
   * @template Model - The Tellescope model name (must support query methods)
   * @template Method - The query method for the model
   * @template TError - The error type for the queries (defaults to Error)
   *
   * @param args - Configuration object containing model, method, optional request, and filters
   * @returns void
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * // Remove all queries for a method
   * queryClient.removeQueries({
   *   model: "users",
   *   method: "getOne",
   * });
   *
   * // Remove specific query
   * queryClient.removeQueries({
   *   model: "users",
   *   method: "getOne",
   *   request: { id: "user-123" },
   * });
   *
   * // Remove stale queries
   * queryClient.removeQueries({
   *   model: "users",
   *   method: "getOne",
   *   filters: {
   *     stale: true,
   *   },
   * });
   * ```
   */
  removeQueries: <Model extends ModelName, Method extends QueryMethodForModel<Model>, TError = Error>(
    args: TellescopeQueryClientFunctionArguments<RemoveTellescopeQueriesArgs<Model, Method, TError>>
  ) => ReturnType<typeof removeTellescopeQueries<Model, Method, TError>>;

  /**
   * Removes all cached queries for a specific Tellescope model.
   *
   * This function provides a way to remove all queries for a model, optionally
   * with method-specific filters. When no methodFilters are provided, it removes
   * all queries for the model. When methodFilters are provided, it only removes
   * queries for the specified methods.
   *
   * @template Model - The Tellescope model name (must support query methods)
   * @template TError - The error type for the queries (defaults to Error)
   *
   * @param args - Configuration object containing model, queryClient, and optional filters
   * @returns void
   *
   * @example
   * ```ts
   * const queryClient = useTellescopeQueryClient();
   *
   * // Remove all user queries
   * queryClient.removeModelQueries({
   *   model: "users",
   * });
   * ```
   *
   * @example
   * ```ts
   * const queryClient = useTellescopeQueryClient();
   *
   * // Remove only specific methods
   * queryClient.removeModelQueries({
   *   model: "users",
   *   methodFilters: {
   *     getOne: {
   *       // Remove all getOne queries
   *     },
   *     getSome: {
   *       request: { filter: { fname: "John" }, limit: 10 },
   *     },
   *   },
   * });
   * ```
   *
   * @example
   * ```ts
   * const queryClient = useTellescopeQueryClient();
   *
   * // Use global filters with method-specific filters
   * queryClient.removeModelQueries({
   *   model: "users",
   *   filters: {
   *     stale: true, // Only remove stale queries
   *   },
   *   methodFilters: {
   *     getOne: {
   *       // Inherits stale: true from global filters
   *     },
   *   },
   * });
   * ```
   */
  removeModelQueries: <Model extends ModelName, TError = Error>(
    args: TellescopeQueryClientFunctionArguments<RemoveTellescopeModelQueriesArgs<Model, TError>>
  ) => ReturnType<typeof removeTellescopeModelQueries<Model, TError>>;

  /**
   * Returns the number of queries that are currently fetching for matching Tellescope queries.
   * Useful for showing global loading indicators or tracking ongoing requests.
   *
   * @template Model - The Tellescope model name (must support query methods)
   * @template Method - The query method for the model
   * @template TError - The error type for the queries (defaults to Error)
   *
   * @param args - Configuration object containing model, method, optional request, and filters
   * @returns The number of queries currently fetching
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * // Check if any queries for a method are fetching
   * const fetchingCount = queryClient.isFetching({
   *   model: "users",
   *   method: "getOne",
   * });
   *
   * // Check specific query
   * const isFetching = queryClient.isFetching({
   *   model: "users",
   *   method: "getOne",
   *   request: { id: "user-123" },
   * }) > 0;
   *
   * // Check with filters
   * const activeFetchingCount = queryClient.isFetching({
   *   model: "users",
   *   method: "getSome",
   *   filters: {
   *     stale: true,
   *   },
   * });
   * ```
   */
  isFetching: <Model extends ModelName, Method extends QueryMethodForModel<Model>, TError = Error>(
    args: TellescopeQueryClientFunctionArguments<IsFetchingTellescopeQueriesArgs<Model, Method, TError>>
  ) => ReturnType<typeof isFetchingTellescopeQueries<Model, Method, TError>>;

  /**
   * Returns the number of mutations that are currently running for matching Tellescope mutations.
   * Useful for showing global loading indicators or tracking ongoing mutation operations.
   *
   * @template Model - The Tellescope model name (must support mutation methods)
   * @template Method - The mutation method for the model
   * @template TError - The error type for the mutations (defaults to Error)
   *
   * @param args - Configuration object containing model, method, and optional filters
   * @returns The number of mutations currently running
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * // Check if any mutations for a method are running
   * const mutatingCount = queryClient.isMutating({
   *   model: "users",
   *   method: "createOne",
   * });
   *
   * // Check if creating users
   * const isCreatingUser = queryClient.isMutating({
   *   model: "users",
   *   method: "createOne",
   * }) > 0;
   *
   * // Check with filters
   * const pendingMutations = queryClient.isMutating({
   *   model: "users",
   *   method: "updateOne",
   *   filters: {
   *     status: "pending",
   *   },
   * });
   * ```
   */
  isMutating: <Model extends ModelName, Method extends MutationMethodForModel<Model>, TError = Error>(
    args: TellescopeQueryClientFunctionArguments<IsMutatingTellescopeMutationsArgs<Model, Method, TError>>
  ) => ReturnType<typeof isMutatingTellescopeMutations<Model, Method, TError>>;

  /**
   * Gets the default options for a specific Tellescope query.
   * Returns the default query options object. If no defaults have been set, returns an empty object.
   *
   * @template Model - The Tellescope model name (must support query methods)
   * @template Method - The query method for the model
   *
   * @param args - Configuration object containing model, method, and optional request
   * @returns The default query options
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * const defaults = queryClient.getQueryDefaults({
   *   model: "users",
   *   method: "getOne",
   * });
   * ```
   */
  getQueryDefaults: <Model extends ModelName, Method extends QueryMethodForModel<Model>>(
    args: TellescopeQueryClientFunctionArguments<GetTellescopeQueryDefaultsArgs<Model, Method>>
  ) => ReturnType<typeof getTellescopeQueryDefaults<Model, Method>>;

  /**
   * Sets default options for matching Tellescope queries.
   * These defaults will be applied to all queries that match the specified key.
   *
   * @template Model - The Tellescope model name (must support query methods)
   * @template Method - The query method for the model
   * @template TError - The error type for the queries
   * @template TData - The transformed data type
   *
   * @param args - Configuration object containing model, method, optional request, and defaults
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * queryClient.setQueryDefaults({
   *   model: "users",
   *   method: "getOne",
   *   defaults: {
   *     staleTime: 5000,
   *     gcTime: 10000,
   *   },
   * });
   * ```
   */
  setQueryDefaults: <
    Model extends ModelName,
    Method extends QueryMethodForModel<Model>,
    TError = Error,
    TData = QueryDataForMethod<Model, Method>,
  >(
    args: TellescopeQueryClientFunctionArguments<SetTellescopeQueryDefaultsArgs<Model, Method, TError, TData>>
  ) => ReturnType<typeof setTellescopeQueryDefaults<Model, Method, TError, TData>>;

  /**
   * Gets the default options for a specific Tellescope mutation.
   * Returns the default mutation options object. If no defaults have been set, returns an empty object.
   *
   * @template Model - The Tellescope model name (must support mutation methods)
   * @template Method - The mutation method for the model
   *
   * @param args - Configuration object containing model and method
   * @returns The default mutation options
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * const defaults = queryClient.getMutationDefaults({
   *   model: "users",
   *   method: "createOne",
   * });
   * ```
   */
  getMutationDefaults: <Model extends ModelName, Method extends MutationMethodForModel<Model>>(
    args: TellescopeQueryClientFunctionArguments<GetTellescopeMutationDefaultsArgs<Model, Method>>
  ) => ReturnType<typeof getTellescopeMutationDefaults<Model, Method>>;

  /**
   * Sets default options for matching Tellescope mutations.
   * These defaults will be applied to all mutations that match the specified key.
   *
   * @template Model - The Tellescope model name (must support mutation methods)
   * @template Method - The mutation method for the model
   * @template TError - The error type for the mutation
   * @template TData - The data type returned by the mutation
   * @template TVariables - The variables type for the mutation
   * @template TOnMutateResult - The context type for the mutation
   *
   * @param args - Configuration object containing model, method, and defaults
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * queryClient.setMutationDefaults({
   *   model: "users",
   *   method: "createOne",
   *   defaults: {
   *     retry: 3,
   *   },
   * });
   * ```
   */
  setMutationDefaults: <
    Model extends ModelName,
    Method extends MutationMethodForModel<Model>,
    TError = Error,
    TData = ReturnForMethod<Model, Method>,
    TVariables = ParamsForMethod<Model, Method>,
    TOnMutateResult = unknown,
  >(
    args: TellescopeQueryClientFunctionArguments<
      SetTellescopeMutationDefaultsArgs<Model, Method, TError, TData, TVariables, TOnMutateResult>
    >
  ) => ReturnType<typeof setTellescopeMutationDefaults<Model, Method, TError, TData, TVariables, TOnMutateResult>>;

  // ============================================================================
  // SESSION QUERY CLIENT METHODS
  // ============================================================================

  /**
   * Fetches a session query and returns the data.
   * This method will always make a network request, regardless of cache state.
   *
   * @template Method - The session query method name
   * @template TError - The error type (defaults to Error)
   * @template TData - The transformed data type (defaults to raw return type)
   *
   * @param args - Configuration object containing method, request, and optional query options
   * @returns A promise that resolves to the query data
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * // Fetch sync data
   * const syncData = await queryClient.fetchSessionQuery({
   *   method: "sync",
   *   request: { from: new Date("2024-01-01") },
   * });
   * ```
   */
  fetchSessionQuery: <
    Method extends SessionStandardQueryMethodName,
    TError = Error,
    TData = SessionMethodReturn<Method>,
  >(
    args: TellescopeQueryClientFunctionArguments<FetchSessionQueryArgs<Method, TError, TData>>
  ) => ReturnType<typeof fetchSessionQuery<Method, TError, TData>>;

  /**
   * Retrieves cached data for a single session query.
   * Returns undefined if the query is not in the cache.
   *
   * @template Method - The session method name
   *
   * @param args - Configuration object containing method and request
   * @returns The cached data or undefined
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * // Get cached sync data
   * const syncData = queryClient.getSessionQueryData({
   *   method: "sync",
   *   request: { from: new Date("2024-01-01") },
   * });
   * ```
   */
  getSessionQueryData: <Method extends SessionMethodName>(
    args: TellescopeQueryClientFunctionArguments<GetSessionQueryDataArgs<Method>>
  ) => ReturnType<typeof getSessionQueryData<Method>>;

  /**
   * Sets cached data for a single session query.
   * Can be used to update the cache directly without making a network request.
   *
   * @template Method - The session method name
   *
   * @param args - Configuration object containing method, request, updater, and optional options
   * @returns An object containing the original query data before update and the updated query data
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * // Set data directly
   * const { originalQuery, updatedQuery } = queryClient.setSessionQueryData({
   *   method: "sync",
   *   request: { from: new Date("2024-01-01") },
   *   updater: { results: [...] },
   * });
   *
   * // Update existing data with an updater function
   * queryClient.setSessionQueryData({
   *   method: "sync",
   *   request: { from: new Date("2024-01-01") },
   *   updater: (old) => old ? { ...old, results: [...old.results, newResult] } : undefined,
   * });
   * ```
   */
  setSessionQueryData: <Method extends SessionMethodName>(
    args: TellescopeQueryClientFunctionArguments<SetSessionQueryDataArgs<Method>>
  ) => ReturnType<typeof setSessionQueryData<Method>>;

  /**
   * Invalidates session queries in the cache.
   * Invalidated queries will be marked as stale and refetched in the background if they are currently being rendered.
   *
   * @template Method - The session query method name
   *
   * @param args - Configuration object containing method, optional request, and filters
   * @returns A promise that resolves when invalidation is complete
   *
   * @example
   * ```tsx
   * const queryClient = useTellescopeQueryClient();
   *
   * // Invalidate all queries for a method
   * await queryClient.invalidateSessionQueries({
   *   method: "sync",
   * });
   *
   * // Invalidate specific query
   * await queryClient.invalidateSessionQueries({
   *   method: "sync",
   *   request: { from: new Date("2024-01-01") },
   * });
   * ```
   */
  invalidateSessionQueries: <Method extends SessionQueryMethodName>(
    args: TellescopeQueryClientFunctionArguments<InvalidateSessionQueriesArgs<Method>>
  ) => ReturnType<typeof invalidateSessionQueries<Method>>;
}

/**
 * A hook that provides Tellescope-specific wrappers around TanStack Query's query client methods.
 * These wrappers automatically inject the session and query client, and configure queries with Tellescope-specific settings.
 *
 * @param providedSession - Optional session to use instead of the context session
 * @returns The tanstack queryClient with Tellescope type injection where possible
 *
 * @example
 * ```tsx
 * const { fetchQuery, getQueryData, ensureQueryData } = useTellescopeQueryClient();
 *
 * // Fetch a query
 * const user = await fetchQuery({
 *   model: "users",
 *   method: "getOne",
 *   request: { id: "user-123" },
 * });
 *
 * // Get cached query data
 * const cachedUser = getQueryData({
 *   model: "users",
 *   method: "getOne",
 *   request: { id: "user-123" },
 * });
 *
 * // Ensure query data exists (fetch if not cached)
 * const ensuredUser = await ensureQueryData({
 *   model: "users",
 *   method: "getOne",
 *   request: { id: "user-123" },
 * });
 * ```
 */
export const useTellescopeQueryClient = (providedSession?: SupportedSession): TellescopeQueryClient => {
  const queryClient = useQueryClient();
  const session = useTellescopeSession(providedSession);
  const resolvedConfig = useTellescopeTanstackConfigContext();

  const fetchQuery = useCallback(
    <
      Model extends ModelName,
      Method extends StandardQueryMethodForModel<Model>,
      TError = Error,
      TData = ReturnForMethod<Model, Method>,
    >(
      args: TellescopeQueryClientFunctionArguments<FetchTellescopeQueryArgs<Model, Method, TError, TData>>
    ) => {
      if (session === null) {
        throw new Error("No Tellescope session provided for query. Queries should not be fetched is session is `null`");
      }

      return fetchTellescopeQuery({
        queryClient,
        session,
        ...args,
      });
    },
    [queryClient, session]
  );

  const fetchInfiniteQuery = useCallback(
    <
      Model extends ModelName,
      Method extends InfiniteQueryMethodForModel<Model>,
      TPageParam,
      TError = Error,
      TData = ReturnForMethod<Model, Method>,
    >(
      args: TellescopeQueryClientFunctionArguments<
        FetchTellescopeInfiniteQueryArgs<Model, Method, TPageParam, TError, TData>
      >
    ) => {
      if (session === null) {
        throw new Error("No Tellescope session provided for query. Queries should not be fetched is session is `null`");
      }

      return fetchInfiniteTellescopeQuery({
        queryClient,
        session,
        minSearchScore: resolvedConfig.minSearchScore,
        ...args,
      });
    },
    [queryClient, session, resolvedConfig.minSearchScore]
  );

  const prefetchQuery = useCallback(
    <
      Model extends ModelName,
      Method extends StandardQueryMethodForModel<Model>,
      TError = Error,
      TData = ReturnForMethod<Model, Method>,
    >(
      args: TellescopeQueryClientFunctionArguments<PrefetchTellescopeQueryArgs<Model, Method, TError, TData>>
    ) => {
      if (session === null) {
        throw new Error("No Tellescope session provided for query. Queries should not be fetched is session is `null`");
      }

      return prefetchTellescopeQuery({
        queryClient,
        session,
        ...args,
      });
    },
    [queryClient, session]
  );

  const prefetchInfiniteQuery = useCallback(
    <
      Model extends ModelName,
      Method extends InfiniteQueryMethodForModel<Model>,
      TPageParam,
      TError = Error,
      TData = ReturnForMethod<Model, Method>,
    >(
      args: TellescopeQueryClientFunctionArguments<
        PrefetchTellescopeInfiniteQueryArgs<Model, Method, TPageParam, TError, TData>
      >
    ) => {
      if (session === null) {
        throw new Error("No Tellescope session provided for query. Queries should not be fetched is session is `null`");
      }

      return prefetchTellescopeInfiniteQuery({
        queryClient,
        session,
        ...args,
      });
    },
    [queryClient, session]
  );

  const getQueryData = useCallback(
    <Model extends ModelName, Method extends MethodForModel<Model>>(
      args: TellescopeQueryClientFunctionArguments<GetTellescopeQueryDataArgs<Model, Method>>
    ) => {
      return getTellescopeQueryData({
        queryClient,
        ...args,
      });
    },
    [queryClient]
  );

  const getQueriesData = useCallback(
    <Model extends ModelName, Method extends QueryMethodForModel<Model>, TError = Error>(
      args: TellescopeQueryClientFunctionArguments<GetTellescopeQueriesDataArgs<Model, Method, TError>>
    ) => {
      return getTellescopeQueriesData({
        queryClient,
        ...args,
      });
    },
    [queryClient]
  );

  const getModelQueriesData = useCallback(
    <Model extends ModelName, TError = Error>(
      args: TellescopeQueryClientFunctionArguments<GetTellescopeModelQueriesDataArgs<Model, TError>>
    ) => {
      return getTellescopeModelQueriesData({
        queryClient,
        ...args,
      });
    },
    [queryClient]
  );

  const getQueryState = useCallback(
    <Model extends ModelName, Method extends MethodForModel<Model>>(
      args: TellescopeQueryClientFunctionArguments<GetTellescopeQueryStateArgs<Model, Method>>
    ) => {
      return getTellescopeQueryState({
        queryClient,
        ...args,
      });
    },
    [queryClient]
  );

  const invalidateQueries = useCallback(
    <Model extends ModelName, Method extends QueryMethodForModel<Model>, TError = Error>(
      args: TellescopeQueryClientFunctionArguments<InvalidateTellescopeQueriesArgs<Model, Method, TError>>
    ) => {
      return invalidateTellescopeQueries({
        queryClient,
        ...args,
      });
    },
    [queryClient]
  );

  const invalidateModelQueries = useCallback(
    <Model extends ModelName, TError = Error>(
      args: TellescopeQueryClientFunctionArguments<InvalidateTellescopeModelQueriesArgs<Model, TError>>
    ) => {
      return invalidateTellescopeModelQueries({
        queryClient,
        ...args,
      });
    },
    [queryClient]
  );

  const setQueryData = useCallback(
    <Model extends ModelName, Method extends QueryMethodForModel<Model>>(
      args: TellescopeQueryClientFunctionArguments<SetTellescopeQueryDataArgs<Model, Method>>
    ) => {
      return setTellescopeQueryData({
        queryClient,
        ...args,
      });
    },
    [queryClient]
  );

  const setQueriesData = useCallback(
    <Model extends ModelName, Method extends QueryMethodForModel<Model>, TError = Error>(
      args: TellescopeQueryClientFunctionArguments<SetTellescopeQueriesDataArgs<Model, Method, TError>>
    ) => {
      return setTellescopeQueriesData({
        queryClient,
        ...args,
      });
    },
    [queryClient]
  );

  const setModelQueriesData = useCallback(
    <Model extends ModelName, TError = Error>(
      args: TellescopeQueryClientFunctionArguments<SetTellescopeModelQueriesDataArgs<Model, TError>>
    ) => {
      return setTellescopeModelQueriesData({
        queryClient,
        ...args,
      });
    },
    [queryClient]
  );

  const ensureQueryData = useCallback(
    <
      Model extends ModelName,
      Method extends StandardQueryMethodForModel<Model>,
      TError = Error,
      TData = ReturnForMethod<Model, Method>,
    >(
      args: TellescopeQueryClientFunctionArguments<EnsureTellescopeQueryDataArgs<Model, Method, TError, TData>>
    ) => {
      if (session === null) {
        throw new Error("No Tellescope session provided for query. Queries should not be ensured is session is `null`");
      }

      return ensureTellescopeQueryData({
        queryClient,
        session,
        ...args,
      });
    },
    [queryClient, session]
  );

  const ensureInfiniteQueryData = useCallback(
    <
      Model extends ModelName,
      Method extends InfiniteQueryMethodForModel<Model>,
      TPageParam,
      TError = Error,
      TData = ReturnForMethod<Model, Method>,
    >(
      args: TellescopeQueryClientFunctionArguments<
        EnsureTellescopeInfiniteQueryDataArgs<Model, Method, TPageParam, TError, TData>
      >
    ) => {
      if (session === null) {
        throw new Error("No Tellescope session provided for query. Queries should not be ensured is session is `null`");
      }

      return ensureTellescopeInfiniteQueryData({
        queryClient,
        session,
        ...args,
      });
    },
    [queryClient, session]
  );

  const cancelQueries = useCallback(
    <Model extends ModelName, Method extends QueryMethodForModel<Model>, TError = Error>(
      args: TellescopeQueryClientFunctionArguments<CancelTellescopeQueriesArgs<Model, Method, TError>>
    ) => {
      return cancelTellescopeQueries({
        queryClient,
        ...args,
      });
    },
    [queryClient]
  );

  const cancelModelQueries = useCallback(
    <Model extends ModelName, TError = Error>(
      args: TellescopeQueryClientFunctionArguments<CancelTellescopeModelQueriesArgs<Model, TError>>
    ) => {
      return cancelTellescopeModelQueries({
        queryClient,
        ...args,
      });
    },
    [queryClient]
  );

  const refetchQueries = useCallback(
    <Model extends ModelName, Method extends QueryMethodForModel<Model>, TError = Error>(
      args: TellescopeQueryClientFunctionArguments<RefetchTellescopeQueriesArgs<Model, Method, TError>>
    ) => {
      return refetchTellescopeQueries({
        queryClient,
        ...args,
      });
    },
    [queryClient]
  );

  const refetchModelQueries = useCallback(
    <Model extends ModelName, TError = Error>(
      args: TellescopeQueryClientFunctionArguments<RefetchTellescopeModelQueriesArgs<Model, TError>>
    ) => {
      return refetchTellescopeModelQueries({
        queryClient,
        ...args,
      });
    },
    [queryClient]
  );

  const resetQueries = useCallback(
    <Model extends ModelName, Method extends QueryMethodForModel<Model>, TError = Error>(
      args: TellescopeQueryClientFunctionArguments<ResetTellescopeQueriesArgs<Model, Method, TError>>
    ) => {
      return resetTellescopeQueries({
        queryClient,
        ...args,
      });
    },
    [queryClient]
  );

  const resetModelQueries = useCallback(
    <Model extends ModelName, TError = Error>(
      args: TellescopeQueryClientFunctionArguments<ResetTellescopeModelQueriesArgs<Model, TError>>
    ) => {
      return resetTellescopeModelQueries({
        queryClient,
        ...args,
      });
    },
    [queryClient]
  );

  const removeQueries = useCallback(
    <Model extends ModelName, Method extends QueryMethodForModel<Model>, TError = Error>(
      args: TellescopeQueryClientFunctionArguments<RemoveTellescopeQueriesArgs<Model, Method, TError>>
    ) => {
      return removeTellescopeQueries({
        queryClient,
        ...args,
      });
    },
    [queryClient]
  );

  const removeModelQueries = useCallback(
    <Model extends ModelName, TError = Error>(
      args: TellescopeQueryClientFunctionArguments<RemoveTellescopeModelQueriesArgs<Model, TError>>
    ) => {
      return removeTellescopeModelQueries({
        queryClient,
        ...args,
      });
    },
    [queryClient]
  );

  const isFetching = useCallback(
    <Model extends ModelName, Method extends QueryMethodForModel<Model>, TError = Error>(
      args: TellescopeQueryClientFunctionArguments<IsFetchingTellescopeQueriesArgs<Model, Method, TError>>
    ) => {
      return isFetchingTellescopeQueries({
        queryClient,
        ...args,
      });
    },
    [queryClient]
  );

  const isMutating = useCallback(
    <Model extends ModelName, Method extends MutationMethodForModel<Model>, TError = Error>(
      args: TellescopeQueryClientFunctionArguments<IsMutatingTellescopeMutationsArgs<Model, Method, TError>>
    ) => {
      return isMutatingTellescopeMutations({
        queryClient,
        ...args,
      });
    },
    [queryClient]
  );

  const getQueryDefaults = useCallback(
    <Model extends ModelName, Method extends QueryMethodForModel<Model>>(
      args: TellescopeQueryClientFunctionArguments<GetTellescopeQueryDefaultsArgs<Model, Method>>
    ) => {
      return getTellescopeQueryDefaults({
        queryClient,
        ...args,
      });
    },
    [queryClient]
  );

  const setQueryDefaults = useCallback(
    <
      Model extends ModelName,
      Method extends QueryMethodForModel<Model>,
      TError = Error,
      TData = QueryDataForMethod<Model, Method>,
    >(
      args: TellescopeQueryClientFunctionArguments<SetTellescopeQueryDefaultsArgs<Model, Method, TError, TData>>
    ) => {
      return setTellescopeQueryDefaults({
        queryClient,
        ...args,
      });
    },
    [queryClient]
  );

  const getMutationDefaults = useCallback(
    <Model extends ModelName, Method extends MutationMethodForModel<Model>>(
      args: TellescopeQueryClientFunctionArguments<GetTellescopeMutationDefaultsArgs<Model, Method>>
    ) => {
      return getTellescopeMutationDefaults({
        queryClient,
        ...args,
      });
    },
    [queryClient]
  );

  const setMutationDefaults = useCallback(
    <
      Model extends ModelName,
      Method extends MutationMethodForModel<Model>,
      TError = Error,
      TData = ReturnForMethod<Model, Method>,
      TVariables = ParamsForMethod<Model, Method>,
      TOnMutateResult = unknown,
    >(
      args: TellescopeQueryClientFunctionArguments<
        SetTellescopeMutationDefaultsArgs<Model, Method, TError, TData, TVariables, TOnMutateResult>
      >
    ) => {
      return setTellescopeMutationDefaults({
        queryClient,
        ...args,
      });
    },
    [queryClient]
  );

  // ============================================================================
  // SESSION QUERY CLIENT METHODS
  // ============================================================================

  const fetchSessionQueryFn = useCallback(
    <Method extends SessionStandardQueryMethodName, TError = Error, TData = SessionMethodReturn<Method>>(
      args: TellescopeQueryClientFunctionArguments<FetchSessionQueryArgs<Method, TError, TData>>
    ) => {
      if (session === null) {
        throw new Error("No Tellescope session provided. Session queries require a valid session.");
      }
      return fetchSessionQuery({
        queryClient,
        session,
        ...args,
      });
    },
    [queryClient, session]
  );

  const getSessionQueryDataFn = useCallback(
    <Method extends SessionMethodName>(
      args: TellescopeQueryClientFunctionArguments<GetSessionQueryDataArgs<Method>>
    ) => {
      return getSessionQueryData({
        queryClient,
        ...args,
      });
    },
    [queryClient]
  );

  const setSessionQueryDataFn = useCallback(
    <Method extends SessionMethodName>(
      args: TellescopeQueryClientFunctionArguments<SetSessionQueryDataArgs<Method>>
    ) => {
      return setSessionQueryData({
        queryClient,
        ...args,
      });
    },
    [queryClient]
  );

  const invalidateSessionQueriesFn = useCallback(
    <Method extends SessionQueryMethodName>(
      args: TellescopeQueryClientFunctionArguments<InvalidateSessionQueriesArgs<Method>>
    ) => {
      return invalidateSessionQueries({
        queryClient,
        ...args,
      });
    },
    [queryClient]
  );

  return {
    fetchQuery,
    fetchInfiniteQuery,
    prefetchQuery,
    prefetchInfiniteQuery,
    getQueryData,
    getQueriesData,
    setQueryData,
    setQueriesData,
    setModelQueriesData,
    getModelQueriesData,
    getQueryState,
    invalidateQueries,
    invalidateModelQueries,
    ensureQueryData,
    ensureInfiniteQueryData,
    cancelQueries,
    cancelModelQueries,
    refetchQueries,
    refetchModelQueries,
    resetQueries,
    resetModelQueries,
    removeQueries,
    removeModelQueries,
    isFetching,
    isMutating,
    getQueryDefaults,
    setQueryDefaults,
    getMutationDefaults,
    setMutationDefaults,
    fetchSessionQuery: fetchSessionQueryFn,
    getSessionQueryData: getSessionQueryDataFn,
    setSessionQueryData: setSessionQueryDataFn,
    invalidateSessionQueries: invalidateSessionQueriesFn,
  };
};
